import {
  SequentialFileUploader,
  UploadableFile,
} from "@/providers/FileTransfer";
import { checkFileSize } from "@/providers/FileTransfer/getUploadItems";
import {
  RobotoDomainException,
  RobotoIllegalArgumentException,
} from "@/shared/http";
import { APIServiceError } from "@/types";

const noop = () => {};

/**
 * Upload `file` as one of the org's own files (associated with the org rather
 * than a dataset or device) at `path`, in an upload transaction of its own
 * (begin, credentials, S3 upload, complete), so a failure names one file.
 * Resolves once the transaction is complete.
 *
 * Throws before any request when the file is over the web upload size limit,
 * as a `RobotoIllegalArgumentException` carrying the limit's own message, so
 * `userFacingErrorMessage` shows it. A refused begin, credentials or complete
 * request is thrown as the `RobotoDomainException` the server's error body
 * describes, so the server's own message shows for a client error (an HTTP
 * 4xx response); any other failure is thrown as is.
 */
export async function uploadOrgFile(
  file: File,
  orgId: string,
  path: string,
): Promise<void> {
  try {
    checkFileSize(file.size, file.name);
  } catch (error) {
    throw new RobotoIllegalArgumentException({
      error_code: RobotoIllegalArgumentException.name,
      message: error instanceof Error ? error.message : String(error),
    });
  }
  try {
    // With no prefix, the manifest key and the upload mapping key are both
    // `path`.
    await new SequentialFileUploader(path, noop, noop, noop).uploadFileBatch(
      true,
      [new UploadableFile(file, path, path)],
      orgId,
      orgId,
      "",
    );
  } catch (error) {
    // A refused begin or credentials request arrives as a plain Error whose
    // cause is the APIServiceError built from the server's error body; a
    // refused complete request arrives as that APIServiceError itself.
    const refusal =
      error instanceof Error && error.cause instanceof APIServiceError
        ? error.cause
        : error;
    if (refusal instanceof APIServiceError) {
      throw RobotoDomainException.fromHttpResponse({
        error_code: refusal.errorCode ?? "Error",
        message: refusal.message,
      });
    }
    throw error;
  }
}
